// ServiceBookings.js — redesigned to match Profile page aesthetic
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import {
  Grid, InputAdornment, TextField, Typography,
  IconButton, Chip, Box, Card, CardContent, Avatar,
  Button, MenuItem, Select, FormControl, Checkbox, ListItemText,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import FilterListIcon from '@mui/icons-material/FilterList';
import SaveIcon from '@mui/icons-material/Save';

import ConfirmDialog from '../../shared/components/ConfirmDialog';
import { toast } from 'react-toastify';
import { fetchList, createRecord } from '../../apis/services/CommonApiService';
import { ApiEndPoints } from '../../apis/ApiEndPoints';

// ─── Status map ───────────────────────────────────────────────────────────────
const serviceStatusText = {
  0: 'Pending',
  1: 'Confirmed',
  2: 'Cancelled',
  3: 'Sample Collected',
  4: 'Report Delivered',
};

const statusChipStyles = {
  0: { bg: '#fff8e1', text: '#f57f17' },   // Pending — amber
  1: { bg: '#e8f5e9', text: '#2e7d32' },   // Confirmed — green
  2: { bg: '#fce4ec', text: '#c62828' },   // Cancelled — red
  3: { bg: '#e3f2fd', text: '#1565c0' },   // Sample Collected — blue
  4: { bg: '#ede7f6', text: '#4527a0' },   // Report Delivered — purple
};

const filterOptions = [
  { value: 'success', label: 'Payment Success' },
  { value: 'pending', label: 'Payment Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: '3', label: 'Sample Collected' },
  { value: '4', label: 'Report Delivered' },
];

const filterLabels = Object.fromEntries(filterOptions.map(f => [f.value, f.label]));

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles({
  root: {
    padding: '30px',
    backgroundColor: '#f4f6f8',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '28px',
  },
  pageTitle: {
    fontWeight: '700',
    color: '#1a2027',
    fontSize: '28px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageTitleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eef4fa',
    borderRadius: '10px',
    width: '44px',
    height: '44px',
    color: '#255480',
  },
  actionBtn: {
    textTransform: 'none',
    fontWeight: '600',
    boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
    borderRadius: '8px',
    padding: '8px 20px',
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, #255480 0%, #1e456e 100%)',
    color: '#fff',
    '&:hover': {
      background: 'linear-gradient(135deg, #1e456e 0%, #255480 100%)',
    },
  },
  statsCard: {
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
    border: 'none',
    overflow: 'hidden',
    height: '100%',
  },
  statValue: {
    fontWeight: '700',
    fontSize: '32px',
    color: '#255480',
    lineHeight: 1.2,
  },
  statLabel: {
    color: '#637381',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '4px',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  dataGridCard: {
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#f9fafb',
      '&:hover fieldset': { borderColor: '#255480' },
      '&.Mui-focused fieldset': { borderColor: '#255480' },
    },
  },
  filterSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#f9fafb',
      '&:hover fieldset': { borderColor: '#255480' },
      '&.Mui-focused fieldset': { borderColor: '#255480' },
    },
  },
  dataGrid: {
    border: 'none !important',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#f4f6f8',
      borderBottom: '1px solid #e8eef4',
      borderRadius: '0 !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: '700',
      color: '#637381',
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    '& .MuiDataGrid-row': {
      '&:hover': { backgroundColor: '#f8faff' },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: '1px solid #f4f6f8',
      fontSize: '14px',
      color: '#212b36',
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: '1px solid #e8eef4',
      backgroundColor: '#fafbfc',
    },
  },
  viewBtn: {
    width: '34px',
    height: '34px',
    borderRadius: '8px',
    backgroundColor: '#eef4fa',
    color: '#255480',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    '&:hover': { backgroundColor: '#255480', color: '#fff' },
  },
  paymentChip: {
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '12px',
    border: 'none',
  },
});

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const s = statusChipStyles[status] || { bg: '#f4f6f8', text: '#637381' };
  return (
    <Chip
      label={serviceStatusText[status] || 'Unknown'}
      size="small"
      style={{ backgroundColor: s.bg, color: s.text, fontWeight: 600, fontSize: '12px', borderRadius: '6px', border: 'none' }}
    />
  );
};

const StatusUpdateCell = ({ row, onUpdate }) => {
  const [status, setStatus] = useState(row.status);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setStatus(row.status);
    setIsDirty(false);
  }, [row.status]);

  const statusOptions = [
    { value: 0, label: 'Pending' },
    { value: 1, label: 'Confirmed' },
    { value: 2, label: 'Cancelled' },
    { value: 3, label: 'Sample Collected' },
    { value: 4, label: 'Report Delivered' }
  ];

  const handleChange = (e) => {
    setStatus(e.target.value);
    setIsDirty(e.target.value !== row.status);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    await onUpdate(row.id, status);
    setIsDirty(false);
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Select
        size="small"
        value={status}
        onChange={handleChange}
        sx={{ minWidth: 140, height: 32, fontSize: '13px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {statusOptions.map(opt => (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '13px' }}>{opt.label}</MenuItem>
        ))}
      </Select>
      {isDirty && (
        <IconButton size="small" color="primary" onClick={handleSave} style={{ padding: 4 }}>
          <SaveIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const PaymentChip = ({ paid }) => (
  <Chip
    icon={<PaymentIcon style={{ fontSize: '14px' }} />}
    label={paid ? 'Paid' : 'Unpaid'}
    size="small"
    style={{
      backgroundColor: paid ? '#e8f5e9' : '#fff8e1',
      color: paid ? '#2e7d32' : '#f57f17',
      fontWeight: 600,
      fontSize: '12px',
      borderRadius: '6px',
      border: 'none',
    }}
  />
);

const StatCard = ({ icon, iconBg, iconColor, value, label, classes }) => (
  <Card className={classes.statsCard}>
    <CardContent style={{ padding: '24px' }}>
      <Box className={classes.statIcon} style={{ backgroundColor: iconBg }}>
        {React.cloneElement(icon, { style: { color: iconColor, fontSize: '22px' } })}
      </Box>
      <Typography className={classes.statValue}>{value}</Typography>
      <Typography className={classes.statLabel}>{label}</Typography>
    </CardContent>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const rowsPerPageJsonData = [5, 10, 20, 50];

const ServiceBookings = () => {
  const navigate = useNavigate();
  const classes = useStyles();

  const [dataGridOptions, setDataGridOptions] = useState({
    loading: false, rows: [], totalRows: 0,
    rowsPerPageOptions: rowsPerPageJsonData, pageSize: 50, page: 1,
  });

  const [searchFilters, setSearchFilters] = useState({ searchText: '' });
  const [bookingFilters, setBookingFilters] = useState(['success']);
  const [allBookings, setAllBookings] = useState([]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = React.useMemo(() => {
    const total = allBookings.length;
    const paid = allBookings.filter(b => b.payment_id !== null).length;
    const collected = allBookings.filter(b => b.booking_status === 3).length;
    const delivered = allBookings.filter(b => b.booking_status === 4).length;
    return { total, paid, collected, delivered };
  }, [allBookings]);

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      field: 'id', headerName: 'Booking ID', flex: 0.7,
      renderCell: (params) => (
        <Typography style={{ fontWeight: 600, color: '#255480', fontSize: '14px' }}>
          #{params.value}
        </Typography>
      ),
    },
    {
      field: 'service_name', headerName: 'Package / Service', flex: 1.4,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar variant="rounded" style={{ width: 30, height: 30, backgroundColor: '#eef4fa', color: '#255480' }}>
            <MedicalServicesIcon style={{ fontSize: '15px' }} />
          </Avatar>
          <Typography style={{ fontWeight: 500, fontSize: '13px', lineHeight: 1.3 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'patient_name', headerName: 'Patient', flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar style={{ width: 30, height: 30, backgroundColor: '#eef4fa', color: '#255480', fontSize: '12px', fontWeight: 700 }}>
            {params.value?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Typography style={{ fontWeight: 500, fontSize: '14px' }}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'service_date', headerName: 'Appointment', flex: 0.9,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.8}>
          <CalendarTodayIcon style={{ fontSize: '13px', color: '#637381' }} />
          <Typography style={{ fontSize: '13px' }}>{params.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'time_slot', headerName: 'Time', flex: 0.8,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.8}>
          <AccessTimeIcon style={{ fontSize: '13px', color: '#637381' }} />
          <Typography style={{ fontSize: '13px' }}>{params.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'created_date', headerName: 'Created', flex: 1,
      valueGetter: (params) =>
        params.row.created_date
          ? new Date(params.row.created_date).toLocaleString('en-IN') : '',
    },
    {
      field: 'payment_id', headerName: 'Payment', flex: 0.8,
      renderCell: (params) => <PaymentChip paid={params.value !== null} />,
    },
    {
      field: 'status', headerName: 'Status', flex: 1.2,
      renderCell: (params) => <StatusUpdateCell row={params.row} onUpdate={handleUpdateStatus} />,
    },
    {
      field: 'actions', type: 'actions', headerName: 'Actions', flex: 0.5,
      renderCell: (params) => (
        <IconButton
          size="small"
          className={classes.viewBtn}
          onClick={() => navigate(`/service-bookings/view/${params.id}`)}
          title="View"
        >
          <VisibilityIcon style={{ fontSize: '16px' }} />
        </IconButton>
      ),
    },
  ];

  // ── Data fetching ──────────────────────────────────────────────────────────
  const getBookingsList = useCallback(async () => {
    setDataGridOptions(prev => ({ ...prev, loading: true }));
    const result = await fetchList(`${ApiEndPoints.PACKAGE_BOOKINGS}?page_no=1&items_per_page=10000`);
    if (result.status === 200) {
      setAllBookings(result.data.rows || []);
    } else {
      toast.error('Failed to fetch service bookings');
    }
    setDataGridOptions(prev => ({ ...prev, loading: false }));
  }, []);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const data = { status: String(newStatus) };
      const result = await createRecord(data, `${ApiEndPoints.PACKAGE_BOOKINGS}/${id}`);
      if (result.status === 200) {
        toast.success('Status updated successfully');
        getBookingsList();
      } else {
        toast.error(result.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const computeRows = useCallback(() => {
    let mapped = allBookings.map(item => ({
      id: item.id,
      service_name:
        item.packageInfo?.name ||
        item.addons_snapshot?.map(a => a.name).join(', ') ||
        'Basic Service',
      patient_name: item.patient_name || '',
      service_date: item.booked_date,
      time_slot: item.slot,
      status: item.booking_status,
      created_date: item.created_at,
      payment_id: item.payment_id,
    }));

    mapped = mapped.filter(row => {
      if (bookingFilters.length === 0) return true;
      return bookingFilters.some(filter => {
        if (filter === 'success') return row.payment_id !== null;
        if (filter === 'pending') return row.payment_id === null;
        const status = filter === 'cancelled' ? 2 : parseInt(filter);
        if (!isNaN(status)) return row.status === status;
        return false;
      });
    });

    const searched = mapped.filter(r => {
      const searchTerm = searchFilters.searchText.toLowerCase().trim();
      return (
        r.patient_name?.toLowerCase().includes(searchTerm) ||
        String(r.id).toLowerCase().includes(searchTerm.replace(/^#/, ''))
      );
    });

    const sorted = searched.sort(
      (a, b) => new Date(b.created_date) - new Date(a.created_date)
    );

    setDataGridOptions(prev => ({ ...prev, rows: sorted, totalRows: sorted.length }));
  }, [allBookings, searchFilters.searchText, bookingFilters]);

  useEffect(() => { getBookingsList(); }, [getBookingsList]);
  useEffect(() => { computeRows(); }, [computeRows]);
  useEffect(() => {
    setDataGridOptions(prev => ({ ...prev, page: 1 }));
  }, [searchFilters.searchText, bookingFilters]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={classes.root}>

      {/* HEADER */}
      <div className={classes.header}>
        <div>
          <Typography className={classes.pageTitle}>
            <span className={classes.pageTitleIcon}>
              <MedicalServicesIcon style={{ fontSize: '22px' }} />
            </span>
            Service Bookings
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
            Manage lab packages, home sample collections and service orders
          </Typography>
        </div>
        <Button
          className={`${classes.actionBtn} ${classes.primaryBtn}`}
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={getBookingsList}
        >
          Refresh
        </Button>
      </div>

      {/* STAT CARDS */}
      <Grid container spacing={3} style={{ marginBottom: '24px' }}>
        {[
          { icon: <MedicalServicesIcon />, iconBg: '#eef4fa', iconColor: '#255480', value: stats.total, label: 'Total Bookings' },
          { icon: <PaymentIcon />, iconBg: '#e8f5e9', iconColor: '#2e7d32', value: stats.paid, label: 'Payment Success' },
          { icon: <LocalShippingIcon />, iconBg: '#e3f2fd', iconColor: '#1565c0', value: stats.collected, label: 'Sample Collected' },
          { icon: <CalendarTodayIcon />, iconBg: '#ede7f6', iconColor: '#4527a0', value: stats.delivered, label: 'Report Delivered' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <StatCard {...s} classes={classes} />
          </Grid>
        ))}
      </Grid>

      {/* TABLE CARD */}
      <Card className={classes.dataGridCard}>
        <CardContent style={{ padding: '24px 24px 0' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={2.5}
          >
            {/* Title + count */}
            <Typography style={{ fontWeight: 700, fontSize: '16px', color: '#1a2027' }}>
              All Service Bookings
              {dataGridOptions.totalRows > 0 && (
                <Chip
                  label={dataGridOptions.totalRows}
                  size="small"
                  style={{ marginLeft: '10px', backgroundColor: '#eef4fa', color: '#255480', fontWeight: 700, fontSize: '12px' }}
                />
              )}
            </Typography>

            {/* Controls */}
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              {/* Filter dropdown */}
              <FormControl size="small" style={{ minWidth: '220px' }}>
                <Select
                  multiple
                  value={bookingFilters}
                  onChange={(e) => setBookingFilters(e.target.value)}
                  displayEmpty
                  className={classes.filterSelect}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? <span style={{ color: '#637381' }}>Filter by status…</span>
                      : selected.map(v => filterLabels[v]).join(', ')
                  }
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterListIcon style={{ fontSize: '16px', color: '#637381', marginLeft: '6px' }} />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: '#f9fafb',
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#255480 !important' },
                    '&.Mui-focused fieldset': { borderColor: '#255480 !important' },
                  }}
                >
                  {filterOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox
                        checked={bookingFilters.includes(option.value)}
                        style={{ color: '#255480' }}
                        size="small"
                      />
                      <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '14px' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Search */}
              <TextField
                size="small"
                placeholder="Search by id or patient name…"
                value={searchFilters.searchText}
                onChange={(e) => setSearchFilters({ searchText: e.target.value })}
                className={classes.searchField}
                style={{ width: '240px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: '#637381', fontSize: '18px' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchFilters.searchText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchFilters({ searchText: '' })}>
                        <CloseIcon style={{ fontSize: '16px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
          </Box>
        </CardContent>

        <DataGrid
          autoHeight
          className={classes.dataGrid}
          loading={dataGridOptions.loading}
          rows={dataGridOptions.rows}
          columns={columns}
          page={dataGridOptions.page - 1}
          pageSize={dataGridOptions.pageSize}
          rowsPerPageOptions={dataGridOptions.rowsPerPageOptions}
          pagination
          disableSelectionOnClick
          onPageChange={(newPage) =>
            setDataGridOptions(prev => ({ ...prev, page: newPage + 1 }))
          }
          onPageSizeChange={(pageSize) =>
            setDataGridOptions(prev => ({ ...prev, page: 1, pageSize }))
          }
        />
      </Card>

      <ConfirmDialog
        openDialog={false}
        handleDialogClose={() => { }}
        title="Confirm"
        message=""
      />
    </div>
  );
};

export default ServiceBookings;